<script setup lang="ts">
import SearchField from './SearchField.vue';

type TSearchMode = 'none' | 'component' | 'panel';

const props = defineProps<{
  /* Показывать поле раскрытым, не дожидаясь клика по лупе */
  expanded?: boolean;
}>();

const { $viewport } = useNuxtApp();

const searchValue = ref('');

const searchMode = ref<TSearchMode>('none');

const searchInputRef = ref<InstanceType<typeof SearchField> | null>(null);

const isNeedChangeTab = inject<Ref<boolean>>('isNeedChangeTab', ref(false));

const getDefaultSearchMode = (): TSearchMode => {
  if (props.expanded && $viewport.isGreaterOrEquals('tabletBig')) {
    return 'component';
  }

  if ($viewport.matches('xs', 'mobile', 'mobileBig', 'tabletBig', 'desktop')) {
    return 'none';
  }

  return 'component';
};

const focusSearchInput = () => {
  nextTick(() => {
    searchInputRef.value?.focusInput();
  });
};

const goToSearch = () => {
  const searchKey = searchValue.value.trim();

  if (!searchKey) {
    return;
  }

  isNeedChangeTab.value = true;
  navigateTo({ path: '/search', query: { key: searchKey } });
  handleResize();
};

const openSearch = () => {
  searchMode.value = 'panel';
  focusSearchInput();
};

const closeSearch = () => {
  searchMode.value = getDefaultSearchMode();
  searchValue.value = '';
};

const switchSearch = () => {
  if (searchMode.value === 'panel') {
    closeSearch();
    return;
  }

  openSearch();
};

/* Из раскрытого поля лупа тоже открывает панель, как было до анонса */
const shouldOpenPanelOnSubmit = () =>
  searchMode.value === 'none' ||
  (props.expanded && searchMode.value !== 'panel');

const handleSubmit = () => {
  if (searchValue.value.trim()) {
    goToSearch();
    return;
  }

  if (searchMode.value === 'panel') {
    closeSearch();
    return;
  }

  if (shouldOpenPanelOnSubmit()) {
    openSearch();
    return;
  }

  focusSearchInput();
};

const handleClearText = () => {
  searchValue.value = '';
  searchInputRef.value?.focusInput();
};

const handleResize = () => {
  searchValue.value = '';
  searchMode.value = getDefaultSearchMode();
};

handleResize();

watch($viewport.breakpoint, () => {
  handleResize();
});

watch(
  () => props.expanded,
  () => {
    if (searchMode.value === 'panel') return;

    searchMode.value = getDefaultSearchMode();
  },
);
</script>

<template>
  <div :class="$style.search" @click.stop="switchSearch">
    <div :class="[{ [$style.search__fixed!]: searchMode === 'panel' }]">
      <div
        :class="[
          $style.search__wrapper,
          {
            [$style.search__panel!]: searchMode === 'panel',
            [$style.search__panel_afterAnnounce!]:
              searchMode === 'panel' && expanded,
            [$style.search__wrapper_inline!]:
              expanded && searchMode !== 'panel',
          },
        ]"
        @click.stop
      >
        <div :class="$style.search__submit" @click="handleSubmit">
          <Icon name="my-icon:i-search" :size="24" />
        </div>
        <SearchField
          v-if="searchMode !== 'none'"
          ref="searchInputRef"
          v-model="searchValue"
          :auto-focus="searchMode === 'panel'"
          :force-full-placeholder="searchMode === 'panel'"
          :class="$style.search__input"
          @begin-search="goToSearch"
        />
        <div
          v-if="searchValue"
          :class="$style.search__clear"
          @click="handleClearText"
        >
          <Icon name="my-icon:i-close" />
        </div>
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.search {
  height: 100%;

  > *:not([class*='fixed']) {
    height: 100%;
  }

  &__fixed {
    position: absolute;
    left: 0;
    width: 100%;
    height: 100vh;
  }

  &__wrapper {
    display: flex;
    flex-flow: row nowrap;
    flex-grow: 1;
    background-color: var(--color-white);
    width: 100%;
    /*
      Не 64px жёстко: слот в шапке на 1px ниже, и белый фон перекрывал
      линию ряда под поиском. Своей границы нет — её рисует ряд.
    */
    height: 100%;
    margin: 0 auto;

    &_inline {
      align-items: center;
    }
  }

  &__submit,
  &__clear,
  &_switcher {
    width: 64px;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    &:hover {
      path {
        fill: var(--color-red-1);
      }
    }
  }

  &_switcher {
    border-right: 1px solid var(--color-gray-line);
  }

  &__panel {
    margin-left: 301px;
    margin-right: 65px;
    width: auto;
    /* Панель лежит поверх страницы, поэтому линия своя */
    height: 64px;
    border-bottom: 1px solid var(--color-gray-line);

    /* Панель начинается там, где кончается плашка анонса */
    &_afterAnnounce {
      margin-left: 955px;
      margin-right: 64px;
      width: auto;
    }
  }
}

@media (min-width: 1024px) and (max-width: 1379px) {
  .search {
    /* Логотип тут 170px, поэтому равняем по его краю: 64 + 170 */
    &__panel {
      margin-left: 234px;
    }

    &__panel_afterAnnounce {
      margin-left: 740px;
    }
  }
}

@media (max-width: 768px) {
  .search {
    &__panel {
      width: 599px;
      margin: 0;
    }

    &__panel_afterAnnounce {
      margin: 0;
    }
  }
}

@media (max-width: 600px) {
  .search {
    &__panel {
      width: 100%;
    }
  }
}
</style>
